import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Box,
  Button,
  ChevronDownIcon,
  GridItem,
  HStack,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from '@cdl/heartbeat-react';
import { TaskMetricSection } from './TaskMetricSection';
import {
  AnnotationTask,
  DataSeries,
  Series,
} from '../../../ecg/types/annotation-tasks.type';
import { getEndOfWeek, getStartOfWeek } from '../../utils/task.utils';
import { DailyCompletionButton } from './DailyCompletionButton';
import {
  annotationsPerUserOptions,
  annotationsTotalOptions,
  EPlotType,
  hoursChartOptions,
} from '../../types/annotation-task-detail.type';

type Props = {
  annotationTask: AnnotationTask;
};

export const DailyCompletion: React.FC<Props> = ({ annotationTask }) => {
  const [filteredData, setFilteredData] = useState(
    annotationTask.userDailyCompletions as Series[],
  );
  const [filteredDataHours, setFilteredDataHours] = useState<Series[]>();
  const [filteredDataTotal, setFilteredDataTotal] = useState<Series[]>();

  const [viewAll, setViewAll] = useState(false);
  const firstDate =
    annotationTask.userDailyCompletions &&
    annotationTask.userDailyCompletions.length > 0
      ? annotationTask.userDailyCompletions[0].data[0].x
      : annotationTask.createdAt;
  const [selectedChart, setSelectedChart] = useState<EPlotType>(
    EPlotType.DailyUserCompletion,
  );

  const onToggleChart = (chart: EPlotType) => setSelectedChart(chart);
  const handleOptionChange = (value: 'currentWeek' | 'all') =>
    setViewAll(value === 'all');

  const filterDataByDate = (startDate: Date, endDate: Date) => {
    const allDates: Date[] = [];

    for (
      startDate;
      startDate <= endDate;
      startDate.setDate(startDate.getDate() + 1)
    ) {
      const dateCopy = new Date(startDate);
      dateCopy.setHours(0, 0, 0, 0);
      allDates.push(dateCopy);
    }

    if (viewAll) {
      startDate.setHours(0, 0, 0, 0);
      if (!allDates.includes(startDate)) {
        allDates.push(startDate);
      }
    }

    const diffDays = allDates.length;
    if (diffDays < 7) {
      for (let i = 1; i <= 7 - diffDays; i++) {
        const newDate = new Date(endDate);
        newDate.setDate(endDate.getDate() + i);
        newDate.setHours(0, 0, 0, 0);
        allDates.push(newDate);
      }
    }

    const completion: Series[] = [];
    const worktime: Series[] = [];
    const total: Series[] = [];

    const getSeriesData = (seriesList: Series[]) => {
      return seriesList.map((series: Series) => {
        const seriesData = allDates.map((timestamp) => {
          const existingData = series.data.find((item) => {
            const itemDate = new Date(item.x);
            itemDate.setHours(0, 0, 0, 0);
            return itemDate.getTime() === timestamp.getTime();
          });
          return {
            x: timestamp,
            y: existingData ? existingData.y : null,
          };
        });

        return {
          name: series.name,
          data: seriesData,
        };
      });
    };

    completion.push(
      ...getSeriesData(annotationTask.userDailyCompletions as Series[]),
    );
    worktime.push(
      ...getSeriesData(annotationTask.userDailyWorktime as Series[]),
    );
    total.push(
      ...getSeriesData([
        {
          name: 'Total Annotations',
          data: annotationTask.totalDailyCompletions as DataSeries[],
        },
      ] as Series[]),
    );

    setFilteredData(completion);
    setFilteredDataHours(worktime);
    setFilteredDataTotal(total);
  };

  useEffect(() => {
    const today = new Date();
    if (!viewAll) {
      const startOfWeek = getStartOfWeek(today);
      const endOfWeek = getEndOfWeek(startOfWeek);
      filterDataByDate(startOfWeek, endOfWeek);
    } else {
      const startDate = new Date(firstDate);
      filterDataByDate(startDate, today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAll]);

  return (
    <GridItem colSpan={2}>
      <TaskMetricSection>
        <HStack w='full' justifyContent='space-between' mb='s'>
          <Text textStyle='category-title'>Daily Completion</Text>
          <Box>
            <DailyCompletionButton
              type={EPlotType.DailyUserCompletion}
              tooltip='Daily annotations per user'
              toggleChart={onToggleChart}
              selectedChart={selectedChart}
            />
            <DailyCompletionButton
              type={EPlotType.DailyUserWorktime}
              tooltip='Daily worktime per user'
              toggleChart={onToggleChart}
              selectedChart={selectedChart}
            />
            <DailyCompletionButton
              type={EPlotType.DailyTotalCompletion}
              tooltip='Daily total annotations'
              toggleChart={onToggleChart}
              selectedChart={selectedChart}
            />
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                variant='secondary'
              >
                {viewAll ? 'Display All' : 'This week'}
              </MenuButton>
              <MenuList>
                <MenuOptionGroup
                  defaultValue={viewAll ? 'all' : 'currentWeek'}
                  type='radio'
                  onChange={(value) =>
                    handleOptionChange(value as 'all' | 'currentWeek')
                  }
                >
                  <MenuItemOption value='currentWeek'>This week</MenuItemOption>
                  <MenuItemOption value='all'>Display All</MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          </Box>
        </HStack>

        {selectedChart === EPlotType.DailyUserCompletion ? (
          <ReactApexChart
            series={filteredData}
            options={annotationsPerUserOptions}
            type='bar'
            height={350}
          />
        ) : selectedChart === EPlotType.DailyTotalCompletion ? (
          <ReactApexChart
            series={filteredDataTotal}
            options={annotationsTotalOptions}
            height={350}
          />
        ) : (
          <ReactApexChart
            series={filteredDataHours}
            options={hoursChartOptions}
            height={350}
          />
        )}
      </TaskMetricSection>
    </GridItem>
  );
};
